import { Injectable, inject } from '@angular/core';
import { FormBuilder, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { EnergySourceInformation } from '@nx-customer-apps/api-planning-projects';
import { CountryCode, EnergySource, EnergySourcesOrder } from '@nx-customer-apps/shared/enums';
import { Mask } from '@nx-customer-apps/components';
import { FormToolsService } from './form-tools.service';
import { ValidatorsService } from './validators.service';
import { RollOutPolicy } from '../policies';
import { EnumDictionary } from '@nx-customer-apps/shared/utils';

@Injectable({
    providedIn: 'root'
})
export class EnergyPricesFormService {
    private formBuilder = inject(FormBuilder);
    private validatorsService = inject(ValidatorsService);
    private formToolsService = inject(FormToolsService);
    private rolloutPolicy = inject(RollOutPolicy);

    public buildForm(energyPrices: EnergySourceInformation[], countryCode: CountryCode): FormGroup {
        return this.formBuilder.group({
            energyPrices: this.formBuilder.array(
                this.orderEnergyPrices(energyPrices).map(price => {
                    const selectedValidationSetting = this.getValidationSettings(price.energySource as EnergySource, countryCode);

                    return this.formBuilder.group({
                        energyCosts: this.formBuilder.control(price.energyCosts, [
                            Validators.required,
                            selectedValidationSetting.energyCostsValidator
                        ]),
                        priceIncrease: this.formBuilder.control(price.priceIncrease, [
                            Validators.required,
                            selectedValidationSetting.priceIncreaseValidator
                        ])
                    });
                })
            )
        });
    }

    public getMaskSettings(addressCountryCode: CountryCode): { [key: string]: Mask } {
        const costLimitsPolicy = this.rolloutPolicy.getEnergyCostsLimits(addressCountryCode);
        return this.supportedEnergySources.reduce(
            (acc, curr) => {
                acc[curr] = this.formToolsService.getNumericMask({
                    separatorLimit: costLimitsPolicy[curr]!.separatorLimit,
                    numberOfDecimals: '2'
                });
                return acc;
            },
            {} as Partial<EnumDictionary<EnergySource, Mask>>
        );
    }

    public orderEnergyPrices(energyPrices: EnergySourceInformation[] | undefined): EnergySourceInformation[] {
        const orderedEnergyPrices: EnergySourceInformation[] = [];
        energyPrices?.forEach(energyPrice => {
            const newTariff = { ...energyPrice, priceIncrease: this.convertNumberToPercentage(energyPrice.priceIncrease) };
            const orderKey = EnergySourcesOrder[energyPrice.energySource as keyof typeof EnergySourcesOrder];
            orderedEnergyPrices[orderKey] = newTariff;
        });
        return orderedEnergyPrices;
    }

    public getValidationSettings(
        energySource: EnergySource,
        addressCountryCode: CountryCode
    ): { energyCostsValidator: ValidatorFn; priceIncreaseValidator: ValidatorFn } {
        const costLimitsPolicy = this.rolloutPolicy.getEnergyCostsLimits(addressCountryCode);

        const allSettings: Partial<
            EnumDictionary<EnergySource, { energyCostsValidator: ValidatorFn; priceIncreaseValidator: ValidatorFn }>
        > = this.supportedEnergySources.reduce(
            (acc, curr) => {
                acc[curr] = {
                    energyCostsValidator: this.validatorsService.minMaxValidator({
                        min: costLimitsPolicy[curr]!.min,
                        max: costLimitsPolicy[curr]!.max
                    }),
                    priceIncreaseValidator: this.validatorsService.minMaxValidator({ min: -10, max: 10 })
                };
                return acc;
            },
            {} as EnumDictionary<EnergySource, { energyCostsValidator: ValidatorFn; priceIncreaseValidator: ValidatorFn }>
        );

        return allSettings[energySource]!;
    }

    private get supportedEnergySources() {
        return [
            EnergySource.HeatingOil,
            EnergySource.Gas,
            EnergySource.Electricity,
            EnergySource.HeatPumpElectricity,
            EnergySource.Pellet,
            EnergySource.Softwood,
            EnergySource.Hardwood
        ];
    }

    private convertNumberToPercentage(priceIncrease: number): number {
        return Math.round((priceIncrease * 100 + Number.EPSILON) * 100) / 100;
    }
}
